import cn from 'classnames'
import { FormFieldValidationLabel } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/formFieldValidationLabel/FormFieldValidationLabel'
import { InvestmentSource } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/investmentSource/InvestmentSource'
import { InvestmentSum } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/investmentSum/InvestmentSum'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { updateFormDataField, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Checkbox } from 'core-components/library/checkbox'
import { Grid, Item } from 'core-components/library/grid'
import { LoaderOverlay } from 'core-components/library/loader'
import { KeyInitiativeMdfSection } from 'partner360/features/keyInitiativeEditor/ui/mdfSection/KeyInitiativeMdfSection'
import { P360Currency } from 'partner360/models/P360Currency'

import { TRANSLATION_KEYS as tr } from '../../../../../../translations'
import { IKeyInitiativeSectionProps } from '../../../models/IKeyInitiativeSectionProps'
import { IKeyInitiativeForm } from '../../../models/keyInitiativeForm'

import styles from './FoundingSection.module.scss'

export const FoundingSection = (props: IKeyInitiativeSectionProps) => {
  const { initialData, initiativeFormKey, partnerId, fYear, programType, saveAsNew } = props

  /** -------------------- State ----------------------- */

  const [isInvestmentNeeds, setIsInvestmentNeeds] = useState(
    initialData?.isInvestmentNeeds ??
      [initialData?.hpInvestmentSource, initialData?.hpInvestmentSum].some(Boolean)
  )

  /** -------------------- Selectors ----------------------- */

  const translationMode = useSelector(selectTranslationMode)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const [{ formData, invalidFields }] = useGetFormData<IKeyInitiativeForm>(
    initiativeFormKey,
    initialData
  )

  /** -------------------- Variables ----------------------- */

  const isTranslationMode = useMemo(() => translationMode === 'edit', [translationMode])
  const hasFundingValidationError = useMemo(
    () =>
      isInvestmentNeeds &&
      Boolean(invalidFields?.hpInvestmentSum || invalidFields?.hpInvestmentSource),
    [isInvestmentNeeds, invalidFields?.hpInvestmentSum, invalidFields?.hpInvestmentSource]
  )

  /** -------------------- Methods ----------------------- */

  const handleInvestmentNeedsChange = useCallback(
    (value: boolean) => {
      setIsInvestmentNeeds(value)
      dispatch(
        updateFormDataField({
          field: 'isInvestmentNeeds',
          value,
          formKey: initiativeFormKey,
        })
      )
    },
    [dispatch, initiativeFormKey]
  )

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    if (typeof formData?.isInvestmentNeeds === 'boolean') {
      setIsInvestmentNeeds(formData.isInvestmentNeeds)
    }
  }, [formData?.isInvestmentNeeds])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <LoaderOverlay show={false} />

      <Grid noRootMargin direction="row">
        <Item xs={12}>
          <Checkbox
            disabled={initialData?.isFromGlobalPartner}
            checked={isInvestmentNeeds}
            label={t(tr.keyInitiativeEditor.investmentFieldGroup.groupHint)}
            onChange={handleInvestmentNeedsChange}
          />
        </Item>
      </Grid>

      {isInvestmentNeeds && (
        <Grid
          noRootMargin
          direction="row"
          className={cn(styles.fieldsetContainer, { [styles.invalid]: hasFundingValidationError })}
        >
          <Item xs={6} className={styles.labelItem}>
            <FormFieldValidationLabel
              text={tr.keyInitiativeEditor.investmentFieldGroup.investmentSumLabel}
              formKey={initiativeFormKey}
              fields={['hpInvestmentSum']}
            />
          </Item>
          <Item xs={6} className={styles.labelItem}>
            <FormFieldValidationLabel
              text={tr.keyInitiativeEditor.investmentFieldGroup.investmentSourceLabel}
              formKey={initiativeFormKey}
              fields={['hpInvestmentSource']}
            />
          </Item>

          <Item xs={6}>
            <InvestmentSum
              isDisabled={initialData?.isFromGlobalPartner}
              isPositiveRequired
              formKey={initiativeFormKey}
              initialValue={initialData?.hpInvestmentSum}
              className={styles.formField}
              placeholder={tr.keyInitiativeEditor.investmentFieldGroup.investmentSumPlaceholder}
            />
            {isTranslationMode && (
              <Translation
                text={tr.keyInitiativeEditor.investmentFieldGroup.investmentSumPlaceholder}
              />
            )}
          </Item>
          <Item xs={6}>
            <InvestmentSource
              isDisabled={initialData?.isFromGlobalPartner}
              formKey={initiativeFormKey}
              initialValue={initialData?.hpInvestmentSource}
              className={styles.formField}
              placeholder={tr.keyInitiativeEditor.investmentFieldGroup.investmentSourcePlaceholder}
            />
            {isTranslationMode && (
              <Translation
                text={tr.keyInitiativeEditor.investmentFieldGroup.investmentSourcePlaceholder}
              />
            )}
          </Item>
        </Grid>
      )}
      <KeyInitiativeMdfSection
        initiativeId={saveAsNew ? undefined : initialData?.entryId}
        partnerId={partnerId}
        fYear={fYear}
        programType={programType}
        currency={formData?.currency || initialData?.currency || P360Currency.USD}
        period={formData?.period ?? initialData?.period ?? []}
        businessUnit={formData?.businessUnit ?? initialData?.businessUnit ?? ''}
        focusArea={formData?.focusArea ?? initialData?.focusArea ?? ''}
      />
    </div>
  )
}
